/**
 * WarehouseReceiptDialog — Sprint Canonical Shipment Dossier (26 Feb 2026).
 *
 * Preview + verstuur Warehouse Receipt vanuit een shipment.
 * - Toont exact wat de klant ontvangt (from, to, subject, bijlage-naam, HTML preview).
 * - Verstuurt via het bestaande /warehouse-receipt/email endpoint.
 * - Bewaart automatisch een snapshot van de verzonden HTML in email_log.
 *
 * Geen tweede composer engine — hergebruikt de bestaande email_service pipeline.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Loader2, Send, Download, ExternalLink, FileText, Mail } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

const LANG_OPTIONS = [
  { code: "nl", label: "Nederlands" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

export default function WarehouseReceiptDialog({ open, onOpenChange, shipment }) {
  const [step, setStep] = useState("preview"); // preview | sending | sent
  const [language, setLanguage] = useState("nl");
  const [toEmail, setToEmail] = useState("");
  const [cc, setCc] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const sid = shipment?.id;
  const wNumber = shipment?.tracking_number;
  const globalLoading = useGlobalLoading();
  // Hard guard tegen dubbelklikken tijdens PDF bekijken/downloaden.
  const pdfInflight = useRef(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Default recipient uit ontvanger of klant
  useEffect(() => {
    if (!shipment) return;
    const recipient = shipment.recipient || {};
    setToEmail(recipient.email || "");
  }, [shipment]);

  const loadPreview = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const r = await adminApi.post(
        `/admin/shipping-labels/${sid}/warehouse-receipt/email/preview`,
        {
          to: toEmail || "preview@example.com",
          cc: cc ? cc.split(",").map((s) => s.trim()).filter(Boolean) : [],
          language,
        },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Preview laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [sid, toEmail, cc, language]);

  useEffect(() => {
    if (open && sid) loadPreview();
  }, [open, sid, language, loadPreview]);

  const handlePrintPdf = async () => {
    // Universele Loading Overlay + double-click guard voor de volledige
    // duur van "Bekijken" — vanaf klik tot PDF-tab daadwerkelijk geopend
    // is (of definitief mislukt). Batch 3 UX-fix.
    if (!sid || pdfInflight.current) return;
    pdfInflight.current = true;
    setPdfBusy(true);
    try {
      await globalLoading.runTask({
        id: `wr-print-${sid}-${language}`,
        message: "Warehouse Receipt wordt geladen…",
        successMessage: "Warehouse Receipt geopend in een nieuw tabblad.",
        run: async () => {
          const r = await adminApi.get(
            `/admin/shipping-labels/${sid}/warehouse-receipt/pdf?language=${language}`,
            { responseType: "blob" },
          );
          const blob = new Blob([r.data], { type: "application/pdf" });
          const objectUrl = URL.createObjectURL(blob);
          const win = window.open(objectUrl, "_blank", "noopener,noreferrer");
          if (!win) {
            // popup blocked — laat de gebruiker weten en clean up
            URL.revokeObjectURL(objectUrl);
            throw new Error(
              "De browser heeft het nieuwe tabblad geblokkeerd. Sta popups toe voor deze site en probeer opnieuw.",
            );
          }
          setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
        },
      });
    } catch (e) {
      // runTask geeft errors door naar de overlay ('failure'). We tonen hier
      // een extra toast met een leesbare fallback zodat de gebruiker altijd
      // weet wat er mis ging (Warehouse Receipt genereren mislukt).
      toast.error(e?.message || formatApiError(e) || "PDF openen mislukt");
    } finally {
      pdfInflight.current = false;
      setPdfBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!sid || pdfInflight.current) return;
    pdfInflight.current = true;
    setPdfBusy(true);
    try {
      await globalLoading.runTask({
        id: `wr-download-${sid}-${language}`,
        message: "Warehouse Receipt wordt gegenereerd…",
        successMessage: "Warehouse Receipt gedownload.",
        run: async () => {
          const r = await adminApi.get(
            `/admin/shipping-labels/${sid}/warehouse-receipt/pdf?language=${language}`,
            { responseType: "blob" },
          );
          const blob = new Blob([r.data], { type: "application/pdf" });
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = `Warehouse-Receipt-${wNumber}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 15_000);
        },
      });
    } catch (e) {
      toast.error(e?.message || formatApiError(e) || "PDF downloaden mislukt");
    } finally {
      pdfInflight.current = false;
      setPdfBusy(false);
    }
  };

  const handleSend = async () => {
    if (!toEmail) {
      toast.error("Vul een ontvanger in");
      return;
    }
    setSending(true);
    try {
      const r = await adminApi.post(
        `/admin/shipping-labels/${sid}/warehouse-receipt/email`,
        {
          to: toEmail,
          cc: cc ? cc.split(",").map((s) => s.trim()).filter(Boolean) : [],
          language,
        },
      );
      const status = r.data?.status;
      if (status === "sent") {
        toast.success(`Warehouse Receipt verzonden naar ${toEmail}`);
      } else if (status === "skipped_no_key") {
        toast.warning(
          "E-mail niet verzonden — Resend API-key ontbreekt in preview omgeving. In productie werkt het wel.",
          { duration: 6000 },
        );
      } else if (status === "skipped_suppressed") {
        toast.error("Ontvanger is uitgeschreven — niet verzonden");
      } else {
        toast.info(`Status: ${status}`);
      }
      setStep("sent");
    } catch (e) {
      toast.error(formatApiError(e) || "Versturen mislukt");
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setStep("preview");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="warehouse-receipt-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-black text-[#082c50] font-chivo flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Warehouse Receipt · {wNumber}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Preview en verstuur de Warehouse Receipt PDF voor deze zending.
            Kies de taal, open de PDF of verstuur hem naar de klant.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Sidebar — actions & fields */}
          <div className="space-y-4 lg:col-span-1">
            <div className="border border-gray-200 rounded-md p-4 space-y-3 bg-gray-50">
              <div>
                <Label className="text-xs font-bold uppercase text-gray-600">Taal</Label>
                <div className="flex gap-1 mt-1">
                  {LANG_OPTIONS.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => setLanguage(opt.code)}
                      data-testid={`wr-lang-${opt.code}`}
                      className={`px-2 py-1 text-xs border transition ${
                        language === opt.code
                          ? "bg-[#082c50] text-white border-[#082c50]"
                          : "bg-white border-gray-300 hover:border-[#082c50]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3 space-y-2">
                <Button
                  onClick={handlePrintPdf}
                  variant="outline"
                  disabled={pdfBusy}
                  className="w-full justify-start rounded-none text-xs disabled:opacity-60"
                  data-testid="wr-btn-print"
                >
                  {pdfBusy
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <ExternalLink className="w-3.5 h-3.5 mr-2" />}
                  {pdfBusy ? "Bezig met laden…" : "PDF openen / afdrukken"}
                </Button>
                <Button
                  onClick={handleDownloadPdf}
                  variant="outline"
                  disabled={pdfBusy}
                  className="w-full justify-start rounded-none text-xs disabled:opacity-60"
                  data-testid="wr-btn-download"
                >
                  {pdfBusy
                    ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    : <Download className="w-3.5 h-3.5 mr-2" />}
                  {pdfBusy ? "Bezig met laden…" : "PDF downloaden"}
                </Button>
              </div>
            </div>

            <div className="border border-gray-200 rounded-md p-4 space-y-3">
              <div className="text-xs font-bold uppercase text-gray-600 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Versturen naar klant
              </div>
              <div>
                <Label className="text-xs">Aan</Label>
                <Input
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  placeholder="klant@voorbeeld.com"
                  className="text-xs mt-1"
                  data-testid="wr-input-to"
                />
              </div>
              <div>
                <Label className="text-xs">CC (optioneel)</Label>
                <Input
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="cc1@x, cc2@x"
                  className="text-xs mt-1"
                  data-testid="wr-input-cc"
                />
              </div>
              <Button
                onClick={loadPreview}
                variant="ghost"
                size="sm"
                className="w-full text-xs rounded-none"
                data-testid="wr-btn-refresh-preview"
              >
                Preview vernieuwen
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !toEmail || step === "sent"}
                className="w-full rounded-none text-xs bg-[#0055FF] hover:bg-[#0044CC] text-white"
                data-testid="wr-btn-send"
              >
                {sending ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Verzenden…</>
                ) : (
                  <><Send className="w-3.5 h-3.5 mr-2" /> Verstuur nu</>
                )}
              </Button>
              {step === "sent" && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 p-2"
                      data-testid="wr-sent-confirmation">
                  Verzonden — de klant ontvangt de Warehouse Receipt als PDF-bijlage. De volledige e-mail is opgeslagen in het Communicatiecentrum onder W-nummer {wNumber}.
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="text-xs font-bold uppercase text-gray-600 mb-2">
              E-mail preview — exact wat de klant ontvangt
            </div>
            {loading || !preview ? (
              <div className="text-center py-16 text-gray-400 border border-gray-200 rounded-md">
                <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
                Preview laden…
              </div>
            ) : (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 p-3 text-xs space-y-1"
                      data-testid="wr-email-headers">
                  <div><span className="text-gray-500 w-16 inline-block">Van:</span> <strong>{preview.from_email}</strong></div>
                  <div><span className="text-gray-500 w-16 inline-block">Aan:</span> <strong>{preview.to}</strong></div>
                  {preview.cc?.length > 0 && (
                    <div><span className="text-gray-500 w-16 inline-block">CC:</span> {preview.cc.join(", ")}</div>
                  )}
                  <div><span className="text-gray-500 w-16 inline-block">Onderwerp:</span> <strong>{preview.subject}</strong></div>
                  <div><span className="text-gray-500 w-16 inline-block">Bijlage:</span>
                    {preview.attachments?.map((a, i) => (
                      <span key={i} className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 rounded text-[11px]">
                        <FileText className="w-3 h-3" /> {a.filename}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="max-h-[500px] overflow-y-auto" data-testid="wr-email-body">
                  <iframe
                    title="Email preview"
                    srcDoc={preview.html}
                    className="w-full h-[500px] border-0"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="rounded-none text-xs"
                   data-testid="wr-btn-close">
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
